/** The decoration layer: the thing that stops terrain reading as tiles.
 *
 * The positional variant already breaks up the fill, but a field of grass with
 * nothing on it still reads as a texture rather than as ground. Small flat
 * clutter - tufts, flowers, pebbles, ripples, cracks - scattered by a hash of
 * the cell coordinate fixes that for the price of a few hundred sprites, and
 * because the hash is the only input the scatter is stable across frames,
 * identical for every player, and free of any RNG stream.
 *
 * Decals are painted into the terrain chunk texture, not kept as live sprites:
 * they never move, they never depth-sort against anything (they are flat), and
 * baking them costs one draw per chunk instead of one per decal per frame.
 *
 * ## They cast shadows, and they did not use to
 *
 * The first version gave map objects a contact shadow and left this layer
 * without one. Overlaying the shadow mask on our own adventure screenshot: the
 * cabin, the wagon, the obelisk, the gem pile and the tree stump had a shadow;
 * the bushes, flower clumps, grass tufts, crystal clusters and the mushroom
 * ring did not. By COUNT that is most of the decoration in the frame, and the
 * eye duly sorted the scene into two populations - things standing on the
 * ground and things printed on it - which is exactly the sticker read the
 * shadows existed to kill.
 *
 * A decal shadow is the same silhouette, sheared by the same light, from
 * `shapeShadow`; it is baked into the chunk beside the decal instead of pooled,
 * because a decal never moves. Sharing the one function is the point: two
 * places computing "where the light is" is how a frame ends up with two suns.
 *
 * Placement policy lives in `scatter.ts`; this file is what draws it.
 */

import { Sprite } from "pixi.js";
import type { Container } from "pixi.js";
import type { Atlas } from "../atlas.js";
import { TILE_SIZE } from "../coords.js";
import type { CellRect } from "../coords.js";
import { terrainName } from "./dualgrid.js";
import { frameBounds } from "./frames.js";
import { FLAT_DECALS, decalAt } from "./scatter.js";
import { shadowStrength, shapeShadow } from "./shadows.js";
import type { DecalRule } from "./scatter.js";
import type { TerrainId } from "./dualgrid.js";

export { DECAL_RULES, decalAt, hash32 } from "./scatter.js";
export type { DecalKind, DecalPlacement, DecalRule } from "./scatter.js";

export interface DecalSurface {
  readonly width: number;
  readonly height: number;
  readonly terrain: Uint8Array;
  readonly road: Uint8Array;
  readonly river: Uint8Array;
  readonly visibility: Uint8Array;
}

export interface DecalOptions {
  readonly rules?: Readonly<Record<TerrainId, DecalRule>>;
}

/**
 * Paint every decal whose cell falls in `rect` into `scene`, positioned
 * relative to `origin` (the chunk's world-pixel top-left).
 *
 * Returns how many were drawn, which is what the debug overlay reports.
 */
export function paintDecals(
  scene: Container,
  atlas: Atlas,
  surface: DecalSurface,
  z: number,
  rect: CellRect,
  origin: { readonly x: number; readonly y: number },
  opts: DecalOptions = {},
): number {
  const plane = surface.width * surface.height * z;
  let drawn = 0;

  for (let y = rect.y0; y < rect.y1; y++) {
    for (let x = rect.x0; x < rect.x1; x++) {
      const i = plane + y * surface.width + x;
      // Unexplored ground has a zeroed terrain byte, which is water; scattering
      // coral over it would leak the map shape through the fog.
      if ((surface.visibility[i] ?? 0) === 0) continue;
      // A road or a river already occupies the cell's centre.
      if ((surface.road[i] ?? 0) !== 0 || (surface.river[i] ?? 0) !== 0) continue;

      const terrain = terrainName(surface.terrain[i] ?? 0);
      const placement = opts.rules === undefined
        ? decalAt(x, y, terrain, TILE_SIZE)
        : decalAt(x, y, terrain, TILE_SIZE, opts.rules);
      if (placement === null) continue;

      const f = atlas.frame(placement.key);
      if (f === null) continue;
      const b = frameBounds(placement.groundX, placement.groundY, f);
      const tex = atlas.texture(placement.key);

      // A puddle is a mark on the ground, not a thing standing on it.
      if (!FLAT_DECALS.has(placement.key)) {
        const shadow = new Sprite(tex);
        shapeShadow(shadow, {
          // Chunk-local, like everything else in this scene; the shear itself
          // is in world units, so it needs no correction.
          groundX: Math.round(placement.groundX - origin.x),
          groundY: Math.round(placement.groundY - origin.y),
          frame: f,
          texture: tex,
          strength: shadowStrength(f, TILE_SIZE) * placement.alpha,
        });
        scene.addChild(shadow);
      }

      const s = new Sprite(tex);
      // Round in world space: a decal at a half pixel is the same texel-blur
      // bug as an unsnapped camera, one sprite at a time.
      s.position.set(Math.round(b.x - origin.x), Math.round(b.y - origin.y));
      s.alpha = placement.alpha;
      scene.addChild(s);
      drawn++;
    }
  }
  return drawn;
}
